import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/common";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { JOB_STATUSES, eur } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, MapPin, Package, Ruler, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LavoroDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [j, setJ] = useState(null);
  const [fin, setFin] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`); setJ(data);
    if (can("view_payments")) { try { const f = await api.get(`/jobs/${id}/finance`); setFin(f.data); } catch {} }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!j) return <p className="text-muted-foreground animate-pulse">Caricamento…</p>;

  const setStatus = async (s) => { await api.put(`/jobs/${id}`, { status: s }); toast.success("Stato aggiornato"); load(); };
  const openEdit = () => { setForm({ product: j.product, description: j.description, measures: j.measures, site_address: j.site_address, agreed_amount: j.agreed_amount, notes: j.notes }); setEdit(true); };
  const saveEdit = async () => {
    try { await api.put(`/jobs/${id}`, form); setEdit(false); toast.success("Lavoro aggiornato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito. I dati inseriti sono stati mantenuti."); }
  };
  const remove = async () => {
    if (!window.confirm(`Eliminare il lavoro "${j.product}"?\n\nL'operazione è definitiva e rimuove solo questo lavoro. Preventivi, incassi e cliente collegati NON vengono eliminati.`)) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Lavoro eliminato"); nav("/lavori"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  return (
    <div>
      <button onClick={() => nav("/lavori")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-brand"><ArrowLeft size={16} /> Lavori</button>

      <div className="card-soft p-5 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-head text-2xl font-extrabold">{j.product}</h1>
            <p className="text-muted-foreground">{j.client?.name}</p>
            {j.description && <p className="text-sm mt-2">{j.description}</p>}
            <div className="mt-3 space-y-1 text-sm text-muted-foreground">
              {j.measures && <p className="flex items-center gap-2"><Ruler size={14} /> {j.measures}</p>}
              {j.site_address && <p className="flex items-center gap-2"><MapPin size={14} /> {j.site_address}</p>}
              {j.materials?.length > 0 && <p className="flex items-center gap-2"><Package size={14} /> {j.materials.join(", ")}</p>}
            </div>
            {j.notes && <p className="mt-3 text-sm bg-accent rounded-lg p-3">{j.notes}</p>}
          </div>
          <div className="text-right">
            {can("view_prices") && <p className="font-head text-2xl font-extrabold">{eur(j.agreed_amount)}</p>}
            <div className="mt-2"><StatusBadge list={JOB_STATUSES} value={j.status} /></div>
            {can("edit_jobs") && (
              <div className="flex gap-2 mt-3 justify-end">
                <Button variant="outline" size="sm" data-testid="btn-edit-job" onClick={openEdit}><Pencil size={14} className="mr-1" /> Modifica</Button>
                <Button variant="outline" size="sm" data-testid="btn-delete-job" onClick={remove} className="text-destructive border-destructive/40 hover:bg-red-50"><Trash2 size={14} className="mr-1" /> Elimina</Button>
              </div>
            )}
          </div>
        </div>

        {can("edit_jobs") && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Aggiorna stato:</span>
            <Select value={j.status} onValueChange={setStatus}>
              <SelectTrigger className="w-56" data-testid="job-status-select"><SelectValue /></SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{JOB_STATUSES.map((s)=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="job-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Modifica lavoro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Prodotto</Label><Input data-testid="edit-job-product" value={form.product||""} onChange={(e)=>setForm({...form,product:e.target.value})} /></div>
            <div><Label>Descrizione</Label><Textarea value={form.description||""} onChange={(e)=>setForm({...form,description:e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Misure</Label><Input value={form.measures||""} onChange={(e)=>setForm({...form,measures:e.target.value})} /></div>
              {can("view_prices") && <div><Label>Importo €</Label><Input type="number" value={form.agreed_amount||""} onChange={(e)=>setForm({...form,agreed_amount:e.target.value})} /></div>}
            </div>
            <div><Label>Indirizzo cantiere</Label><Input value={form.site_address||""} onChange={(e)=>setForm({...form,site_address:e.target.value})} /></div>
            <div><Label>Note</Label><Textarea value={form.notes||""} onChange={(e)=>setForm({...form,notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEdit(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="edit-job-save" onClick={saveEdit}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {can("view_payments") && fin && (
        <div className="card-soft p-5 mb-5" data-testid="job-finance">
          <h3 className="font-head font-bold mb-3">Incassi — schema 40/40/20</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div><p className="text-xs text-muted-foreground">Concordato</p><p className="font-bold">{eur(fin.agreed)}</p></div>
            <div><p className="text-xs text-muted-foreground">Incassato</p><p className="font-bold text-emerald-600">{eur(fin.received)}</p></div>
            <div><p className="text-xs text-muted-foreground">Residuo</p><p className="font-bold text-brand">{eur(fin.residual)}</p></div>
          </div>
          <div className="space-y-2">
            {fin.schedule?.map((s,i)=>(
              <div key={i} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                <span>{s.label}</span><span className="font-medium">{eur(s.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft p-5">
        <AttachmentsPanel jobId={j.id} clientId={j.client_id} title="Allegati del lavoro" />
      </div>
    </div>
  );
}
